//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { ReactNode, useEffect, useState, MouseEvent } from 'react';
import { Grid, GridProps } from '@mui/material';
import Datepicker, { DatepickerProps } from './Datepicker/Datepicker';
import styled from '@emotion/styled';
import { Label } from '../_common/LabelValue';
import PolarisIcon from '@/components/_common/PolarisIcon';

type OnEditFormatter = (date: Date) => string;

export type EditableDateProps = GridProps & {
  label?: string | ReactNode;
  children?: any;
  onEdit: (key: string, value: string | undefined, formatter: OnEditFormatter) => void;
  formatFn?: (val: string) => React.ReactNode;
  onEditFormatter: OnEditFormatter;
  onEditKey: string;
  disabled: boolean;
  value?: string | undefined;
  color: string;
  key: string;
  disablePastDates?: boolean;
  skipConfirmation?: boolean;
  portalId?: DatepickerProps['portalId'];
  maxDate?: Date;
  filterTime?: DatepickerProps['filterTime'];
};

const EditableDate = ({
  value,
  label,
  children,
  onEdit,
  formatFn,
  onEditFormatter,
  onEditKey,
  disabled,
  disablePastDates = false,
  portalId,
  skipConfirmation = false,
  maxDate,
  filterTime,
  ...props
}: EditableDateProps) => {
  const [isEditing, setEditedRowId] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | undefined>(value);

  useEffect(() => {
    setSelectedDate(value);
  }, [value]);

  const handleConfirm = (e: MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    setEditedRowId(false);
    onEdit(onEditKey, selectedDate, onEditFormatter);
  };

  const handleCancel = (e: MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    setEditedRowId(false);
    setSelectedDate(value);
  };

  return (
    <EditableField item {...props}>
      <Grid container>
        <Label color="primary">{label}</Label>
        {!isEditing && !disabled && (
          <EditableIcon
            id={onEditKey}
            icon="edit"
            className="edit-icon"
            height={16}
            onClick={(e) => {
              e.stopPropagation();
              setEditedRowId(true);
            }}
          />
        )}
        {isEditing && (
          <>
            <CancelIcon icon="close_filled" height={18} onClick={handleCancel} />
            <ConfirmIcon icon="complete" height={16} onClick={handleConfirm} />
          </>
        )}
      </Grid>
      {isEditing ? (
        <EditDatepicker
          id={onEditKey}
          label={null}
          value={selectedDate}
          showTimeSelect
          onChange={(date: any) => {
            const value = date ? date?.toISOString() : undefined;
            setSelectedDate(value);
            if (skipConfirmation) {
              onEdit(onEditKey, value, onEditFormatter);
            }
          }}
          minDate={disablePastDates ? new Date() : undefined}
          portalId={portalId}
          maxDate={maxDate ? maxDate : undefined}
          filterTime={filterTime}
        />
      ) : formatFn && value ? (
        formatFn(value)
      ) : (
        children || value || '-'
      )}
    </EditableField>
  );
};

export default EditableDate;

const EditableField = styled(Grid)`
  height: auto;
  border-color: ${({ theme }) => theme.palette.primary.main};
  padding: ${({ theme }) => theme.spacing(0, 0.5)};
  margin-bottom: ${({ theme }) => theme.spacing(-1)};

  &:hover .edit-icon {
    opacity: 1;
  }

  & fieldset {
    height: auto;
  }
`;

const EditDatepicker = styled(Datepicker)`
  margin-bottom: ${({ theme }) => theme.spacing(-2)};
  > div {
    border-color: ${({ theme }) => theme.palette.primary.main};

    & input {
      padding: 0;
    }

    & fieldset {
      height: auto;
    }
  }
`;

const EditableIcon = styled(PolarisIcon)`
  margin-left: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.primary.main};
  transition: ease opacity 200ms;
  cursor: pointer;
  opacity: 0;
`;

const CancelIcon = styled(PolarisIcon)`
  margin-left: ${({ theme }) => theme.spacing(1)};
  transition: ease opacity 200ms;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.default.main};
  opacity: 0.7;

  &:hover {
    opacity: 0.9;
  }
`;

const ConfirmIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.success.main};
  margin-left: ${({ theme }) => theme.spacing(1)};
  transition: ease opacity 200ms;
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 0.8;
  }
`;
